import React from 'react';
import { getDiscountPercentage, isDiscounted, isMemberPrice } from './price.component';
import { ICoreContext } from '@msdyn365-commerce/core';
import { DiscountLine, ProductPrice, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getTranslation, TranslationKey } from '../functions/get-translation';
export interface LabelProduct {
    ItemId: string;
}
export interface LabelProductPrice {
    DiscountLines: DiscountLine[];
    BasePrice: number;
    CustomerContextualPrice: number;
}
export interface ProductLabelsProps {
    product: LabelProduct | SimpleProduct | ProductSearchResult;
    context: ICoreContext;
    price?: LabelProductPrice | ProductPrice;
}
export interface ProductLabelConfiguration {
    displayText?: string;
    locale?: string;
    appliesToAll?: boolean;
    appliesToProducts?: string;
    labelColor?: string;
    labelTextColor?: string;
    startTime?: string;
    endTime?: string;
    customClass?: string;
}
export interface DisplayLabel {
    label?: string;
    color: string;
    textColor: string;
    class?: string;
}

const ProductLabel = ({ product, context, price }: ProductLabelsProps) => {
    const { maxProductLabels } = context.actionContext.requestContext.app.config;
    const labelsForProduct: DisplayLabel[] = [];
    //Calculate functional labels
    if (price) {
        const isDiscount = isDiscounted(price);
        if (isDiscount) {
            const isMemberDiscount = isMemberPrice(price, context);
            if (isMemberDiscount) {
                labelsForProduct.push({
                    color: '#949494',
                    textColor: 'white',
                    label: getTranslation(TranslationKey.MemberPriceMessage, context) ?? 'Members Price'
                });
            } else {
                const discountPercentage = getDiscountPercentage(price);
                labelsForProduct.push({
                    color: '#000000',
                    textColor: 'white',
                    label: `-${discountPercentage.toFixed(0)}%`
                });
            }
        }
    }
    //Get configured labels
    labelsForProduct.push(...getProductLabels(product, context));

    return renderLabels(labelsForProduct, maxProductLabels);
};

function renderLabels(labels: DisplayLabel[], maxProductLabels: number = 3) {
    return (
        <ul className='product-labels'>
            {labels.map((label, index) => {
                if (index > maxProductLabels - 1) {
                    return null;
                }
                const className = label.class ? `product-label ${label.class}` : 'product-label';
                return (
                    <li key={index} className={className} style={{ backgroundColor: label.color, color: label.textColor }}>
                        {label.label}
                    </li>
                );
            })}
        </ul>
    );
}

export function getProductLabels(product: LabelProduct | SimpleProduct | ProductSearchResult, context: ICoreContext, classToFind?: string) {
    const labelsForProduct: DisplayLabel[] = [];
    const { productLabels } = context.actionContext.requestContext.app.config;

    const now = Date.now();
    const locale = context.request.locale;
    if (productLabels) {
        productLabels.forEach((label: ProductLabelConfiguration) => {
            if (classToFind && (!label.customClass || !label.customClass.toLowerCase().includes(classToFind.toLowerCase()))) {
                return;
            }
            if (label.locale && !label.locale.toLowerCase().includes(locale.toLowerCase())) {
                return;
            }

            const start = label.startTime ? new Date(label.startTime).getTime() : null;
            if (start && now < start) {
                return;
            }
            const end = label.endTime ? new Date(label.endTime).getTime() : null;
            if (end && now > end) {
                return;
            }

            if (label.appliesToAll) {
                labelsForProduct.push({
                    label: label.displayText,
                    color: label.labelColor?.trim() ? label.labelColor : '#2266E3',
                    textColor: label.labelTextColor?.trim() ? label.labelTextColor : 'white',
                    class: label.customClass
                });
                return;
            }
            if (label.appliesToProducts && product.ItemId && label.appliesToProducts.includes(product.ItemId)) {
                labelsForProduct.push({
                    label: label.displayText,
                    color: label.labelColor?.trim() ? label.labelColor : '#2266E3',
                    textColor: label.labelTextColor?.trim() ? label.labelTextColor : 'white',
                    class: label.customClass
                });
                return;
            }
        });
    }
    return labelsForProduct;
}
export default ProductLabel;
